import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Clock,
  BookOpen,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Users,
} from 'lucide-react'
import { ctaForProgram, availabilityMeta, fmtDate, fmtDateLong } from './intakeUi'

/**
 * IntakeProgramCard — reusable, intake-aware program card.
 *
 * Renders a single program from the active intake with availability-aware
 * copy and CTAs. Closed programs show "Join waitlist", upcoming programs
 * show "Opens <date>" — never a misleading "Apply now".
 *
 * Props:
 *   program     — a normalised program object (see data/activeIntake.js)
 *   intakeLabel — short label of the parent intake (for the corner badge)
 *   enquiryHref — fallback enquiry link
 */
export default function IntakeProgramCard({ program, intakeLabel, enquiryHref = '/advisor' }) {
  const cta = ctaForProgram(program, enquiryHref)
  const meta = availabilityMeta(program)

  const toneClass =
    meta.tone === 'green'
      ? 'text-green-400'
      : meta.tone === 'amber'
        ? 'text-cinema-amber'
        : meta.tone === 'gold'
          ? 'text-cinema-gold'
          : 'text-cinema-muted'

  return (
    <div className="group flex flex-col h-full bg-white/[0.02] border border-white/5 hover:border-cinema-gold/30 transition-all duration-500 rounded-sm overflow-hidden">
      {/* Badge row */}
      <div className="px-4 sm:px-5 pt-4 sm:pt-5 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cinema-gold/10 border border-cinema-gold/30 text-cinema-gold text-[0.65rem] uppercase tracking-[0.12em] font-sans font-semibold">
          <Calendar className="w-3 h-3" aria-hidden="true" />
          {intakeLabel || 'Intake'}
        </span>
        <span className={`inline-flex items-center gap-1 text-[0.65rem] font-sans font-medium uppercase tracking-wider ${toneClass}`}>
          {meta.tone === 'green' ? (
            <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
          ) : meta.tone === 'amber' ? (
            <AlertCircle className="w-3 h-3" aria-hidden="true" />
          ) : (
            <Calendar className="w-3 h-3" aria-hidden="true" />
          )}
          {meta.label}
        </span>
      </div>

      {/* Image */}
      {program.image && (
        <div className="px-4 sm:px-5 pt-4">
          <div className="cinema-image-wrap aspect-[16/10] rounded-sm overflow-hidden">
            <img
              src={program.image}
              alt={program.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cinema-black/40 via-transparent to-transparent" />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="px-4 sm:px-5 py-4 sm:py-5 flex-1 flex flex-col">
        <h3 className="font-display text-base sm:text-lg text-white font-medium leading-tight">
          {program.title}
        </h3>
        {program.code && (
          <p className="text-[0.7rem] text-cinema-muted font-mono mt-1 uppercase tracking-wider">
            {program.code}
          </p>
        )}

        {program.description && (
          <p className="text-xs text-cinema-soft/70 font-sans mt-3 leading-relaxed line-clamp-3 flex-1">
            {program.description}
          </p>
        )}

        {/* Meta row */}
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-cinema-muted font-sans">
          {program.duration && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-cinema-gold/60" aria-hidden="true" />
              {program.duration}
            </span>
          )}
          {program.level && (
            <span className="flex items-center gap-1">
              <BookOpen className="w-3 h-3 text-cinema-gold/60" aria-hidden="true" />
              {program.level}
            </span>
          )}
          {typeof program.seatsAvailable === 'number' && program.availability !== 'closed' && (
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3 text-cinema-gold/60" aria-hidden="true" />
              {program.seatsAvailable} seat{program.seatsAvailable === 1 ? '' : 's'} left
            </span>
          )}
        </div>

        {/* Fee */}
        {program.fee && (
          <p className="mt-3 font-display text-lg text-cinema-gold font-semibold">{program.fee}</p>
        )}

        {/* Availability / intake schedule */}
        <div className="mt-3 pt-3 border-t border-white/5 text-xs text-cinema-soft/80 font-sans space-y-1">
          {program.availability === 'open' && program.registrationCloseDate && (
            <span className="flex items-center gap-1.5 text-green-400/90 font-medium">
              <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
              Apply by {fmtDateLong(program.registrationCloseDate)}
            </span>
          )}
          {program.availability === 'closing-soon' && program.registrationCloseDate && (
            <span className="flex items-center gap-1.5 text-cinema-amber font-medium">
              <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
              Closing {fmtDateLong(program.registrationCloseDate)}
            </span>
          )}
          {program.availability === 'upcoming' && (
            <>
              {program.registrationOpenDate && (
                <span className="flex items-center gap-1.5 text-cinema-gold/90 font-medium">
                  <Calendar className="w-3.5 h-3.5 text-cinema-gold" aria-hidden="true" />
                  {program.registrationCloseDate
                    ? `Registration: ${fmtDate(program.registrationOpenDate)} – ${fmtDate(program.registrationCloseDate)}`
                    : `Registration opens ${fmtDateLong(program.registrationOpenDate)}`}
                </span>
              )}
              {program.startDate && (
                <p className="pl-5 text-cinema-muted">
                  Program: {fmtDate(program.startDate)} – {fmtDate(program.endDate)}
                </p>
              )}
            </>
          )}
          {program.availability === 'closed' && (
            <span className="flex items-center gap-1.5 text-cinema-muted font-medium">
              <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
              {program.startDate ? `Started ${fmtDate(program.startDate)}` : 'Currently in session'}
            </span>
          )}
          {program.availability === 'fallback' && (
            <span className="text-cinema-muted/60">Next intake dates to be announced</span>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="px-4 sm:px-5 pb-4 sm:pb-5 flex gap-2">
        {program.detailHref && (
          <Link
            to={program.detailHref}
            className="flex-1 cinema-btn-ghost !text-xs !py-2.5 flex items-center justify-center gap-1.5"
          >
            View Program
            <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        )}
        {cta.href ? (
          <Link
            to={cta.href}
            className={`flex-1 flex items-center justify-center gap-1.5 !text-xs !py-2.5 min-h-[40px] rounded-sm transition-colors ${
              cta.kind === 'primary'
                ? 'cinema-btn-primary !text-xs !py-2.5'
                : 'text-cinema-gold/90 font-sans font-medium bg-cinema-gold/10 hover:bg-cinema-gold/20 border border-cinema-gold/30'
            }`}
            aria-label={`${cta.label} — ${program.title}`}
          >
            {cta.label}
          </Link>
        ) : (
          <span className="flex-1 text-center text-xs text-cinema-muted/60 font-sans py-2.5 border border-white/5 rounded-sm">
            {cta.label}
          </span>
        )}
      </div>
    </div>
  )
}
